/**
 * Transportily — Customs Document Generator (Browser / jsPDF)
 *
 * Generates two documents:
 *  1. generateShipmentReceipt(booking, trip)  → per-sender PDF
 *  2. generateCargoManifest(trip, bookings)   → full trip manifest PDF
 *
 * Requires jsPDF + jspdf-autotable loaded in the page (via CDN in index.html)
 * or installed as npm packages:
 *   npm install jspdf jspdf-autotable
 */

// ── Brand ────────────────────────────────────────────────────────────────────
const RED   = [232, 52, 26];
const DARK  = [14, 13, 11];
const SAND  = [245, 237, 216];
const OLIVE = [138, 132, 104];
const LIGHT = [249, 247, 243];
const WHITE = [255, 255, 255];
const BORDER_GRAY = [221, 221, 221];

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmtDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
  } catch { return iso; }
}

function fmtDateTime(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })
      + '  ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

function refCode(prefix, id) {
  return `${prefix}-${(id || '00000000').substring(0, 8).toUpperCase()}`;
}

function generatedAt() {
  return new Date().toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
}

function loadJsPDF() {
  return new Promise((resolve, reject) => {
    if (window.jspdf) return resolve(window.jspdf.jsPDF);
    const script1 = document.createElement('script');
    script1.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
    script1.onload = () => {
      const script2 = document.createElement('script');
      script2.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.2/jspdf.plugin.autotable.min.js';
      script2.onload = () => resolve(window.jspdf.jsPDF);
      script2.onerror = reject;
      document.head.appendChild(script2);
    };
    script1.onerror = reject;
    document.head.appendChild(script1);
  });
}

// ── Shared page chrome ────────────────────────────────────────────────────────
function drawPageChrome(doc, ref, docTypeEn, docTypeFr, pageNum, totalPages) {
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const M = 18;

  // Red header band
  doc.setFillColor(...RED);
  doc.rect(0, 0, W, 26, 'F');

  // Platform name
  doc.setTextColor(...WHITE);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  doc.text('Transportily', M, 10);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.text('Community Cargo Transport Platform  ·  DE ↔ TN', M, 16);
  doc.setFontSize(8);
  doc.text('transportily.com  ·  support@transportily.com', M, 21.5);

  // Doc type (right)
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text(docTypeEn, W - M, 10, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text(docTypeFr, W - M, 16, { align: 'right' });
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.text(`REF: ${ref}`, W - M, 21.5, { align: 'right' });

  // Footer line
  doc.setDrawColor(...BORDER_GRAY);
  doc.line(M, H - 12, W - M, H - 12);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(...OLIVE);
  doc.text(`Generated: ${generatedAt()}`, M, H - 7);
  doc.text(`Page ${pageNum} of ${totalPages}`, W - M, H - 7, { align: 'right' });
  doc.text('This document is generated by Transportily for customs presentation purposes only.', W / 2, H - 7, { align: 'center' });

  // Reset colour
  doc.setTextColor(...DARK);
}

function sectionTitle(doc, y, textEn, textFr) {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...RED);
  doc.text(`${textEn.toUpperCase()}  /  ${textFr.toUpperCase()}`, 18, y);
  doc.setDrawColor(...RED);
  doc.setLineWidth(0.3);
  doc.line(18, y + 1.5, 192, y + 1.5);
  doc.setTextColor(...DARK);
  doc.setLineWidth(0.2);
  doc.setDrawColor(...BORDER_GRAY);
  return y + 6;
}

// ══════════════════════════════════════════════════════════════════════════════
// DOCUMENT 1 — Individual Shipment Receipt
// ══════════════════════════════════════════════════════════════════════════════
export async function generateShipmentReceipt(booking, trip) {
  const JsPDF = await loadJsPDF();
  const doc = new JsPDF({ unit: 'mm', format: 'a4' });

  const W = 210;
  const M = 18;
  const COL = (W - M * 2 - 4) / 2;
  const FULL = W - M * 2;
  const ref = refCode('RCPT', booking.id);

  // ── Page 1 ────────────────────────────────────────────────────────────────
  drawPageChrome(doc, ref, 'SHIPMENT RECEIPT', "REÇU D'EXPÉDITION", 1, 1);

  let y = 32;

  // Title
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text('Shipment Receipt', M, y);
  y += 6;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(...OLIVE);
  doc.text("Reçu d'Expédition — Personal Goods / Effets Personnels Non Commerciaux", M, y);
  doc.setTextColor(...DARK);
  y += 6;
  doc.setDrawColor(...BORDER_GRAY);
  doc.line(M, y, W - M, y);
  y += 6;

  // ── Trip info ─────────────────────────────────────────────────────────────
  y = sectionTitle(doc, y, 'Trip Information', 'Informations du Voyage');

  const transporter = trip.transporter || trip.profiles || {};
  const sender      = booking.sender || booking.profiles || {};

  doc.autoTable({
    startY: y,
    margin: { left: M, right: M },
    theme: 'plain',
    styles: { fontSize: 8.5, cellPadding: { top: 3, bottom: 3, left: 5, right: 5 } },
    columnStyles: { 0: { fontStyle: 'bold', textColor: OLIVE, cellWidth: 58 }, 1: { textColor: DARK } },
    body: [
      ['Trip Reference / Référence voyage',           refCode('TRIP', trip.id)],
      ['Route / Itinéraire',                          `${trip.origin_city}, ${trip.origin_country}  →  ${trip.destination_city}, ${trip.destination_country}`],
      ['Departure Date / Date de départ',             fmtDate(trip.departure_date)],
      ['Goods Collected On / Collecte effectuée le',  fmtDateTime(booking.collected_at || booking.updated_at)],
      ['Receipt Reference / Référence du reçu',       ref],
    ],
    alternateRowStyles: { fillColor: LIGHT },
  });
  y = doc.lastAutoTable.finalY + 6;

  // ── Parties ────────────────────────────────────────────────────────────────
  y = sectionTitle(doc, y, 'Parties', 'Parties');

  // Sender (left) + Transporter (right) side by side
  const senderRows = [
    ['Full Name / Nom', sender.full_name || '—'],
    ['Phone / Téléphone', sender.phone || '—'],
    ['City (DE) / Ville', booking.pickup_address || trip.origin_city || '—'],
    ['Country / Pays', 'Germany / Allemagne'],
  ];
  const transporterRows = [
    ['Full Name / Nom', transporter.full_name || '—'],
    ['Phone / Téléphone', transporter.phone || '—'],
    ['Platform / Plateforme', 'Transportily'],
    ['Country / Pays', 'Germany / Allemagne'],
  ];

  const sideW = FULL / 2 - 2;
  // Left table (sender)
  doc.autoTable({
    startY: y,
    margin: { left: M, right: W / 2 + 2 },
    theme: 'plain',
    styles: { fontSize: 8, cellPadding: { top: 3, bottom: 3, left: 5, right: 4 } },
    headStyles: { fillColor: DARK, textColor: WHITE, fontStyle: 'bold', fontSize: 8 },
    head: [['SENDER / EXPÉDITEUR', '']],
    columnStyles: { 0: { fontStyle: 'bold', textColor: OLIVE, cellWidth: 38 } },
    body: senderRows,
    alternateRowStyles: { fillColor: LIGHT },
  });
  // Right table (transporter)
  doc.autoTable({
    startY: y,
    margin: { left: W / 2 + 2, right: M },
    theme: 'plain',
    styles: { fontSize: 8, cellPadding: { top: 3, bottom: 3, left: 5, right: 4 } },
    headStyles: { fillColor: DARK, textColor: WHITE, fontStyle: 'bold', fontSize: 8 },
    head: [['TRANSPORTER / TRANSPORTEUR', '']],
    columnStyles: { 0: { fontStyle: 'bold', textColor: OLIVE, cellWidth: 38 } },
    body: transporterRows,
    alternateRowStyles: { fillColor: LIGHT },
  });
  y = Math.max(doc.lastAutoTable.finalY, y + 28) + 6;

  // ── Recipient ─────────────────────────────────────────────────────────────
  y = sectionTitle(doc, y, 'Recipient in Tunisia', 'Destinataire en Tunisie');
  doc.autoTable({
    startY: y,
    margin: { left: M, right: M },
    theme: 'plain',
    styles: { fontSize: 8.5, cellPadding: { top: 3, bottom: 3, left: 5, right: 5 } },
    columnStyles: { 0: { fontStyle: 'bold', textColor: OLIVE, cellWidth: 60 }, 1: { textColor: DARK } },
    body: [
      ['Full Name / Nom complet',       booking.recipient_name  || '—'],
      ['Phone / Téléphone',             booking.recipient_phone || '—'],
      ['Delivery Type / Type livraison', (booking.drop_off_type || 'pickup_location').replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase())],
      ['Address / Adresse (TN)',        booking.delivery_address || 'At transporter pickup location'],
      ['City / Ville (TN)',             trip.destination_city || '—'],
    ],
    alternateRowStyles: { fillColor: LIGHT },
  });
  y = doc.lastAutoTable.finalY + 6;

  // ── Goods ─────────────────────────────────────────────────────────────────
  y = sectionTitle(doc, y, 'Description of Goods', 'Description des Marchandises');
  doc.autoTable({
    startY: y,
    margin: { left: M, right: M },
    theme: 'plain',
    styles: { fontSize: 8.5, cellPadding: { top: 4, bottom: 4, left: 5, right: 5 } },
    headStyles: { fillColor: DARK, textColor: WHITE, fontStyle: 'bold', fontSize: 8 },
    head: [['Contents / Contenu', 'Weight / Poids', 'Declared Value / Valeur déclarée', 'Nature']],
    columnStyles: {
      0: { cellWidth: FULL * 0.46 },
      1: { cellWidth: FULL * 0.15, halign: 'center', fontStyle: 'bold' },
      2: { cellWidth: FULL * 0.22, halign: 'center' },
      3: { cellWidth: FULL * 0.17, halign: 'center' },
    },
    body: [[
      [
        booking.description || 'Personal effects / Effets personnels',
        booking.document_count > 0
          ? `📄 ${booking.document_count} × ${
              booking.document_type === 'official'     ? 'Official document' :
              booking.document_type === 'business'     ? 'Business document' :
              booking.document_type === 'letter'       ? 'Personal letter/envelope' :
              booking.document_type === 'small_parcel' ? 'Small parcel (<500g)' :
              'Document'
            }`
          : '',
      ].filter(Boolean).join('\n'),
      booking.kg_booked > 0 ? `${booking.kg_booked} kg` : (booking.document_count > 0 ? `${booking.document_count} items` : '—'),
      `EUR ${parseFloat(booking.total_price || 0).toFixed(2)}`,
      'Personal\nPersonnel',
    ]],
  });
  y = doc.lastAutoTable.finalY + 3;

  doc.setFont('helvetica', 'oblique');
  doc.setFontSize(7.5);
  doc.setTextColor(...OLIVE);
  const declText = doc.splitTextToSize(
    '⚠  The sender declares these goods are personal effects for personal/family use NOT intended for commercial resale. ' +
    "/ L'expéditeur déclare que ces biens sont des effets personnels à usage personnel/familial et ne sont PAS destinés à la revente commerciale.",
    FULL
  );
  doc.text(declText, M, y);
  doc.setTextColor(...DARK);
  y += declText.length * 4 + 5;

  // ── Payment ───────────────────────────────────────────────────────────────
  y = sectionTitle(doc, y, 'Payment Details', 'Détails du Paiement');
  const isCash = booking.sender_payment_mode === 'cash';
  doc.autoTable({
    startY: y,
    margin: { left: M, right: M },
    theme: 'plain',
    styles: { fontSize: 8.5, cellPadding: { top: 3, bottom: 3, left: 5, right: 5 } },
    columnStyles: { 0: { fontStyle: 'bold', textColor: OLIVE, cellWidth: 60 }, 1: { textColor: DARK } },
    body: [
      ['Payment Method / Mode de paiement', isCash ? 'Cash at pickup / Espèces à la collecte (platform fee online)' : 'Online / En ligne via Transportily'],
      ...(booking.kg_booked > 0 ? [['Goods / Marchandises', `${booking.kg_booked} kg × EUR ${parseFloat(booking.total_price && booking.document_count > 0 ? (booking.total_price - (booking.document_count * (booking.document_price || 0))) / (1 + 0.10) : booking.total_price / (1 + 0.10) || 0).toFixed(2)}/kg`]] : []),
      ...(booking.document_count > 0 ? [['Documents', `${booking.document_count} × EUR ${parseFloat(booking.document_price || 0).toFixed(2)}/item`]] : []),
      ['Platform Fee / Frais de plateforme (10%)', `EUR ${parseFloat(booking.platform_fee || 0).toFixed(2)}`],
      ...(isCash ? [
        ['Paid online / Payé en ligne',  `EUR ${parseFloat(booking.platform_fee || 0).toFixed(2)}`],
        ['Due in cash / En espèces',     `EUR ${(parseFloat(booking.total_price || 0) - parseFloat(booking.platform_fee || 0)).toFixed(2)}`],
      ] : [
        ['Transport Fee / Frais de transport', `EUR ${(parseFloat(booking.total_price || 0) - parseFloat(booking.platform_fee || 0)).toFixed(2)}`],
      ]),
      ['Total / Total',  `EUR ${parseFloat(booking.total_price || 0).toFixed(2)}`],
      ['Status / Statut', (booking.payment_status || '—').replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase())],
    ],
    alternateRowStyles: { fillColor: LIGHT },
  });
  y = doc.lastAutoTable.finalY + 6;

  // ── Platform declaration ─────────────────────────────────────────────────
  doc.setDrawColor(...RED);
  doc.setLineWidth(0.5);
  doc.rect(M, y, FULL, 1, 'F'); // top border
  y += 3;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.text('PLATFORM DECLARATION / DÉCLARATION DE LA PLATEFORME', M, y);
  y += 4;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(...OLIVE);
  const platDecl = doc.splitTextToSize(
    'Transportily confirms that this shipment was registered on its platform, the sender\'s identity was verified, ' +
    'and payment was processed. The goods are personal effects transported by a community carrier on behalf of a private individual. ' +
    '/ Transportily confirme que cette expédition a été enregistrée sur sa plateforme, l\'identité de l\'expéditeur a été vérifiée, ' +
    'et le paiement a été traité. Les biens sont des effets personnels transportés par un transporteur communautaire pour le compte d\'un particulier.',
    FULL
  );
  doc.text(platDecl, M, y);
  doc.setTextColor(...DARK);
  y += platDecl.length * 4 + 8;

  // ── Signatures ────────────────────────────────────────────────────────────
  if (y > 240) { doc.addPage(); drawPageChrome(doc, ref, 'SHIPMENT RECEIPT', "REÇU D'EXPÉDITION", 1, 1); y = 32; }
  doc.autoTable({
    startY: y,
    margin: { left: M, right: M },
    theme: 'plain',
    styles: { fontSize: 8, cellPadding: { top: 6, bottom: 22, left: 8, right: 8 } },
    columnStyles: { 0: { cellWidth: FULL / 3 }, 1: { cellWidth: FULL / 3 }, 2: { cellWidth: FULL / 3 } },
    body: [[
      'Transporter Signature\nSignature du transporteur\n\n________________________',
      'Sender Signature\nSignature de l\'expéditeur\n\n________________________',
      'Date of Collection\nDate de collecte\n\n________________________',
    ]],
  });

  // ── Save ──────────────────────────────────────────────────────────────────
  const senderName = (sender.full_name || 'sender').replace(/\s+/g,'-').toLowerCase();
  doc.save(`transportily-receipt-${senderName}-${refCode('',booking.id)}.pdf`);
}


// ══════════════════════════════════════════════════════════════════════════════
// DOCUMENT 2 — Trip Cargo Manifest
// ══════════════════════════════════════════════════════════════════════════════
export async function generateCargoManifest(trip, bookings) {
  const JsPDF = await loadJsPDF();
  const doc = new JsPDF({ unit: 'mm', format: 'a4' });

  const W = 210;
  const M = 18;
  const FULL = W - M * 2;
  const ref = refCode('MNFST', trip.id);
  const transporter = trip.transporter || trip.profiles || {};

  const totalKg    = bookings.reduce((s, b) => s + parseFloat(b.kg_booked  || 0), 0);
  const totalDocs  = bookings.reduce((s, b) => s + parseInt(b.document_count || 0), 0);
  const totalValue = bookings.reduce((s, b) => s + parseFloat(b.total_price || 0), 0);

  // Count pages (rough estimate for footer — we'll use 1 if one table)
  const totalPages = Math.ceil((bookings.length * 12 + 120) / 240) + 1;
  let pageNum = 1;

  const onPage = (data) => {
    if (data.pageCount !== pageNum) {
      pageNum = data.pageCount;
      drawPageChrome(doc, ref, 'CARGO MANIFEST', 'MANIFESTE DE FRET', pageNum, totalPages);
    }
  };

  drawPageChrome(doc, ref, 'CARGO MANIFEST', 'MANIFESTE DE FRET', 1, totalPages);

  let y = 32;

  // Title
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text('Trip Cargo Manifest', M, y);
  y += 6;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(...OLIVE);
  doc.text('Manifeste de Fret du Voyage — Personal Goods Transport / Transport d\'Effets Personnels', M, y);
  doc.setTextColor(...DARK);
  y += 5;
  doc.setDrawColor(...BORDER_GRAY);
  doc.line(M, y, W - M, y);
  y += 6;

  // ── Trip & Transporter ────────────────────────────────────────────────────
  y = sectionTitle(doc, y, 'Trip & Transporter', 'Voyage et Transporteur');
  doc.autoTable({
    startY: y,
    margin: { left: M, right: M },
    theme: 'plain',
    styles: { fontSize: 8.5, cellPadding: { top: 3, bottom: 3, left: 5, right: 5 } },
    columnStyles: { 0: { fontStyle: 'bold', textColor: OLIVE, cellWidth: 65 }, 1: { textColor: DARK } },
    body: [
      ['Manifest Reference / Référence manifeste', ref],
      ['Trip Reference / Référence voyage',         refCode('TRIP', trip.id)],
      ['Route / Itinéraire',
       `${trip.origin_city}, ${trip.origin_country}  →  ${trip.destination_city}, ${trip.destination_country}`],
      ['Departure Date / Date de départ',           fmtDate(trip.departure_date)],
      ['Transporter / Transporteur',                transporter.full_name || '—'],
      ['Transporter Phone / Téléphone',             transporter.phone || '—'],
      ['Transport Type / Type',                     'Private individual community carrier / Particulier transporteur communautaire'],
      ['Total Senders / Nombre d\'expéditeurs',     `${bookings.length}`],
      ['Total Weight / Poids total',                `${totalKg.toFixed(1)} kg`],
      ...(totalDocs > 0 ? [['Total Documents / Documents totaux', `${totalDocs} item(s)`]] : []),
      ['Total Declared Value / Valeur totale déclarée', `EUR ${totalValue.toFixed(2)}`],
      ['Document Generated / Document généré le',  generatedAt()],
    ],
    alternateRowStyles: { fillColor: LIGHT },
  });
  y = doc.lastAutoTable.finalY + 6;

  // ── Goods table ───────────────────────────────────────────────────────────
  y = sectionTitle(doc, y, 'Complete List of Goods', 'Liste Complète des Marchandises');

  const tableRows = bookings.map((b, i) => {
    const sender = b.sender || b.profiles || {};
    const isCash = b.sender_payment_mode === 'cash';
    return [
      String(i + 1),
      `${sender.full_name || '—'}\n${sender.phone || ''}`,
      `${b.recipient_name || '—'}\n${b.recipient_phone || ''}`,
      [
        b.description || 'Personal effects',
        b.document_count > 0
          ? `📄 ${b.document_count} × ${
              b.document_type === 'official'     ? 'Official doc' :
              b.document_type === 'business'     ? 'Business doc' :
              b.document_type === 'letter'       ? 'Letter/envelope' :
              b.document_type === 'small_parcel' ? 'Small parcel' :
              'Document'
            }`
          : '',
      ].filter(Boolean).join('\n'),
      b.kg_booked > 0 ? `${parseFloat(b.kg_booked).toFixed(1)} kg` : (b.document_count > 0 ? `${b.document_count} items` : '—'),
      `EUR ${parseFloat(b.total_price || 0).toFixed(2)}`,
      isCash ? 'Cash\n(fee online)' : 'Online',
      refCode('RCPT', b.id),
    ];
  });

  tableRows.push([
    '', '', '', 'TOTAL', `${totalKg.toFixed(1)} kg`, `EUR ${totalValue.toFixed(2)}`, '', '',
  ]);

  doc.autoTable({
    startY: y,
    margin: { left: M, right: M },
    theme: 'plain',
    styles: { fontSize: 7.5, cellPadding: { top: 3.5, bottom: 3.5, left: 4, right: 4 }, valign: 'top', overflow: 'linebreak' },
    headStyles: { fillColor: DARK, textColor: WHITE, fontStyle: 'bold', fontSize: 7.5, halign: 'left' },
    head: [['#', 'Sender / Expéditeur', 'Recipient / Destinataire', 'Contents / Contenu', 'Weight\nPoids', 'Value\nValeur', 'Payment\nPaiement', 'Receipt Ref']],
    columnStyles: {
      0: { cellWidth: 7, halign: 'center' },
      1: { cellWidth: 34 },
      2: { cellWidth: 34 },
      3: { cellWidth: FULL - 7 - 34 - 34 - 17 - 24 - 18 - 22 },
      4: { cellWidth: 17, halign: 'center', fontStyle: 'bold' },
      5: { cellWidth: 24, halign: 'center' },
      6: { cellWidth: 18, halign: 'center' },
      7: { cellWidth: 22, fontSize: 7 },
    },
    body: tableRows,
    alternateRowStyles: { fillColor: LIGHT },
    didParseCell: (data) => {
      // Bold + top border on totals row
      if (data.row.index === tableRows.length - 1) {
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.fillColor = [240, 237, 232];
        data.cell.styles.lineWidth = { top: 0.5 };
        data.cell.styles.lineColor = DARK;
      }
    },
    didDrawPage: onPage,
  });
  y = doc.lastAutoTable.finalY + 6;

  // ── Declarations ──────────────────────────────────────────────────────────
  // Check if we need a new page
  if (y > 210) { doc.addPage(); drawPageChrome(doc, ref, 'CARGO MANIFEST', 'MANIFESTE DE FRET', ++pageNum, totalPages); y = 32; }

  y = sectionTitle(doc, y, 'Declarations', 'Déclarations');
  const declItems = [
    '•  All goods listed are personal effects, NOT intended for commercial resale / Tous les biens listés sont des effets personnels, PAS destinés à la revente commerciale',
    '•  The transporter is a private individual carrying goods on behalf of other private individuals / Le transporteur est un particulier transportant des biens pour le compte d\'autres particuliers',
    '•  All sender identities were verified by Transportily / Les identités de tous les expéditeurs ont été vérifiées par Transportily',
    '•  Payment for transport services was processed through the Transportily platform / Le paiement a été traité via la plateforme Transportily',
    '•  Individual receipts are available per shipment (see Receipt Ref column above) / Des reçus individuels sont disponibles par expédition (voir colonne Réf. ci-dessus)',
    '•  Goods are carried as personal luggage / as accompanied baggage by a private traveller / Les biens sont transportés comme bagages personnels / bagages accompagnés par un voyageur particulier',
  ];
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(...OLIVE);
  declItems.forEach(item => {
    const lines = doc.splitTextToSize(item, FULL);
    doc.text(lines, M, y);
    y += lines.length * 4 + 2;
  });
  doc.setTextColor(...DARK);
  y += 4;

  // ── Platform declaration box ───────────────────────────────────────────────
  if (y > 220) { doc.addPage(); drawPageChrome(doc, ref, 'CARGO MANIFEST', 'MANIFESTE DE FRET', ++pageNum, totalPages); y = 32; }

  doc.setDrawColor(...RED);
  doc.setLineWidth(0.6);
  doc.setFillColor(255, 248, 246);
  const declBoxText = doc.splitTextToSize(
    'PLATFORM DECLARATION / DÉCLARATION DE LA PLATEFORME\n\n' +
    'Transportily, a digital platform facilitating community cargo transport between Germany and Tunisia, ' +
    'hereby confirms that all shipments listed in this manifest were booked through its platform, ' +
    'sender identities were verified, transport fees were collected, and individual receipts were issued. ' +
    'This manifest is an official document generated by Transportily for customs presentation.\n\n' +
    "Transportily, une plateforme numérique facilitant le transport communautaire de fret entre l'Allemagne et la Tunisie, " +
    "confirme que toutes les expéditions listées ont été réservées via sa plateforme, les identités vérifiées, les frais collectés, " +
    "et les reçus individuels émis. Ce manifeste est un document officiel généré par Transportily à des fins douanières.",
    FULL - 16
  );
  const boxH = declBoxText.length * 4.2 + 10;
  doc.rect(M, y, FULL, boxH, 'FD');
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(...OLIVE);
  doc.text(declBoxText, M + 8, y + 6);
  doc.setTextColor(...DARK);
  y += boxH + 8;

  // ── Signatures ────────────────────────────────────────────────────────────
  if (y > 240) { doc.addPage(); drawPageChrome(doc, ref, 'CARGO MANIFEST', 'MANIFESTE DE FRET', ++pageNum, totalPages); y = 32; }
  doc.autoTable({
    startY: y,
    margin: { left: M, right: M },
    theme: 'plain',
    styles: { fontSize: 8, cellPadding: { top: 6, bottom: 28, left: 8, right: 8 } },
    columnStyles: {
      0: { cellWidth: FULL * 0.38 },
      1: { cellWidth: FULL * 0.24 },
      2: { cellWidth: FULL * 0.38 },
    },
    body: [[
      `Transporter Signature / Signature\n${transporter.full_name || ''}\n\n________________________`,
      'Date\n\n\n________________________',
      'Customs Officer Stamp / Cachet douanier\n\n\n________________________',
    ]],
  });

  // ── Save ──────────────────────────────────────────────────────────────────
  const tripDate = (trip.departure_date || '').substring(0, 10);
  doc.save(`transportily-cargo-manifest-${trip.origin_city || 'trip'}-${tripDate}-${refCode('',trip.id)}.pdf`);
}